<template>
  <div class="page" v-if="character">
    <div class="page-header">
      <h1 class="page-title">Character Details</h1>
      <breadcrumb
        name="ADVOCACY_CHARACTER_DETAIL"
        :withLast.sync="breadcrumbTitle"
      ></breadcrumb>
      <div class="page-header-actions detail-actions">
        <span class="save-badge" :class="saveStateClass">
          {{ saveStateText }}
        </span>
        <button type="button" class="btn btn-round btn-default" @click="goBack">
          <i class="icon md-chevron-left" aria-hidden="true"></i>
          Back to list
        </button>
      </div>
    </div>

    <div class="page-content container-fluid">
      <div class="row">
        <div class="col-xl-12 col-lg-12">
          <div class="panel panel-bordered mb-30">
            <div class="panel-heading">
              <h3 class="panel-title">Character - {{ character.id }}</h3>
            </div>
            <div class="panel-body">
              <div class="form-horizontal">
                <div class="form-group row">
                  <label class="col-sm-2 col-form-label">Character ID</label>
                  <div class="col-sm-6 detail-value">{{ character.id }}</div>
                </div>
                <div class="form-group row">
                  <label class="col-sm-2 col-form-label">Character Name</label>
                  <div class="col-sm-6">
                    <input
                      v-model="form.name"
                      type="text"
                      maxlength="255"
                      class="form-control"
                      @input="onFormInput"
                      @blur="onFormBlur"
                    />
                  </div>
                </div>
                <div class="form-group row mb-0">
                  <label class="col-sm-2 col-form-label">Description</label>
                  <div class="col-sm-6">
                    <textarea
                      v-model="form.description"
                      rows="3"
                      maxlength="255"
                      class="form-control"
                      @input="onFormInput"
                      @blur="onFormBlur"
                    ></textarea>
                  </div>
                </div>
              </div>
              <div class="autosave-note">
                Auto save when leaving a text field.
              </div>
            </div>
          </div>

          <div class="panel panel-bordered mb-30">
            <div class="panel-heading">
              <h3 class="panel-title">Background Image</h3>
            </div>
            <div class="panel-body">
              <div class="upload-card">
                <div class="upload-card-header">
                  <div>
                    <h5 class="mb-5">Background Image</h5>
                    <p class="upload-helper mb-0">
                      PNG only. Recommended size 1869 x 3005 pixels.
                    </p>
                  </div>
                  <span
                    v-if="uploadingField === 'background_url'"
                    class="uploading-text"
                  >
                    Uploading...
                  </span>
                </div>

                <div class="upload-preview-wrapper">
                  <img
                    v-if="character.background_url"
                    :src="character.background_url"
                    alt="Background preview"
                    class="upload-preview"
                  />
                  <div v-else class="preview-placeholder">No image uploaded</div>
                </div>

                <div class="file-input-row">
                  <button
                    type="button"
                    class="btn btn-default btn-sm"
                    :disabled="uploadingField === 'background_url'"
                    @click="openFilePicker('background_url')"
                  >
                    Choose file
                  </button>
                  <span class="file-input-name">
                    {{ selectedFileNames.background_url || 'No file chosen' }}
                  </span>
                </div>

                <input
                  ref="background_url"
                  type="file"
                  accept=".png,image/png"
                  class="file-input-native"
                  :disabled="uploadingField === 'background_url'"
                  @change="uploadImage('background_url', 'Background Image', $event)"
                />
              </div>
            </div>
          </div>

          <div class="panel panel-bordered">
            <div class="panel-heading">
              <h3 class="panel-title">Character Images</h3>
            </div>
            <div class="panel-body">
              <div class="slot-grid">
                <div v-for="slot in animationSlots" :key="slot.number" class="upload-card">
                  <div class="upload-card-header">
                    <div>
                      <h5 class="mb-5">Slot {{ slot.number }}</h5>
                      <p class="upload-helper mb-0">
                        Field {{ slot.field }} · PNG only · 1869 x 3005 pixels
                      </p>
                    </div>
                    <span
                      v-if="uploadingField === slot.field"
                      class="uploading-text"
                    >
                      Uploading...
                    </span>
                  </div>

                  <div class="upload-preview-wrapper">
                    <img
                      v-if="slot.image_url"
                      :src="slot.image_url"
                      :alt="`Character slot ${slot.number}`"
                      class="upload-preview"
                    />
                    <div v-else class="preview-placeholder">No image uploaded</div>
                  </div>

                  <div class="file-input-row">
                    <button
                      type="button"
                      class="btn btn-default btn-sm"
                      :disabled="uploadingField === slot.field"
                      @click="openFilePicker(slot.field)"
                    >
                      Choose file
                    </button>
                    <span class="file-input-name">
                      {{ selectedFileNames[slot.field] || 'No file chosen' }}
                    </span>
                  </div>

                  <input
                    :ref="slot.field"
                    type="file"
                    accept=".png,image/png"
                    class="file-input-native"
                    :disabled="uploadingField === slot.field"
                    @change="uploadImage(slot.field, `Slot ${slot.number}`, $event)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { characterService } from "@/api/client";

const defaultForm = () => ({
  name: "",
  description: "",
});

export default {
  name: "AdvocacyCharacterDetail",

  data() {
    return {
      character: null,
      form: defaultForm(),
      saveState: "saved",
      isInitializing: true,
      uploadingField: null,
      selectedFileNames: {},
      currentSavePromise: null,
    };
  },

  computed: {
    breadcrumbTitle() {
      if (!this.character) return "Character Details";
      return this.form.name || `Character - ${this.character.id}`;
    },

    saveStateClass() {
      return {
        "is-saved": this.saveState === "saved",
        "is-saving": this.saveState === "saving",
        "is-unsaved": this.saveState === "unsaved",
      };
    },

    saveStateText() {
      switch (this.saveState) {
        case "saving":
          return "Saving...";
        case "unsaved":
          return "Unsaved";
        default:
          return "Saved";
      }
    },

    animationMap() {
      const map = {};
      const animations = Array.isArray(this.character && this.character.animation)
        ? this.character.animation
        : [];

      animations.forEach((item) => {
        if (item && item.number) {
          map[Number(item.number)] = item;
        }
      });

      return map;
    },

    animationSlots() {
      return Array.from({ length: 20 }, (_, index) => {
        const number = index + 1;
        const animation = this.animationMap[number] || {};

        return {
          number,
          field: `character${number}`,
          image_url: animation.image_url || "",
        };
      });
    },
  },

  async mounted() {
    await this.fetchCharacter();
  },

  beforeRouteLeave(to, from, next) {
    this.flushPendingSave()
      .then(() => next())
      .catch(() => next());
  },

  methods: {
    async fetchCharacter() {
      this.isInitializing = true;

      try {
        const data = await characterService.getById(this.$route.params.id);
        this.character = data;
        this.form = {
          name: data.name || "",
          description: data.description || "",
        };
        this.saveState = "saved";
      } catch (error) {
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(error.message || error);
      } finally {
        this.$nextTick(() => {
          this.isInitializing = false;
        });
      }
    },

    onFormInput() {
      if (this.isInitializing) return;

      this.saveState = "unsaved";
    },

    async onFormBlur() {
      if (this.isInitializing || this.saveState !== "unsaved") return;
      await this.persistCharacter();
    },

    async persistCharacter() {
      if (!this.character) return;
      if (this.currentSavePromise) {
        await this.currentSavePromise;
        return;
      }

      this.saveState = "saving";

      try {
        const payload = {
          name: this.form.name,
          description: this.form.description,
        };

        this.currentSavePromise = characterService.editCharacter(
          this.character.id,
          payload
        );
        await this.currentSavePromise;
        this.character = Object.assign({}, this.character, payload);
        this.saveState = "saved";
      } catch (error) {
        this.saveState = "unsaved";
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(error.message || error);
      } finally {
        this.currentSavePromise = null;
      }
    },

    async flushPendingSave() {
      if (this.saveState === "saving" && this.currentSavePromise) {
        await this.currentSavePromise;
        return;
      }

      if (this.saveState !== "unsaved") return;

      await this.persistCharacter();
    },

    async goBack() {
      await this.flushPendingSave();
      this.$router.push({ name: "AdvocacyList" }).catch(() => {});
    },

    openFilePicker(field) {
      const inputRef = this.$refs[field];
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;

      if (input) {
        input.click();
      }
    },

    clearInput(field) {
      this.$nextTick(() => {
        const inputRef = this.$refs[field];
        const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;

        if (input) {
          input.value = "";
        }

        this.$set(this.selectedFileNames, field, "");
      });
    },

    isValidPng(file) {
      if (!file) return false;
      if (file.type === "image/png") return true;

      this.$dialogs.alert("File image ต้องเป็น PNG เท่านั้น");
      return false;
    },

    async uploadImage(field, label, event) {
      const file = event.target.files[0];

      if (!file) return;
      if (!this.isValidPng(file)) {
        this.clearInput(field);
        return;
      }

      try {
        this.uploadingField = field;
        this.$set(this.selectedFileNames, field, file.name);

        const form = new FormData();
        form.append(field, file, file.name);

        await characterService.uploadCharacterImage(this.character.id, form);
        await this.fetchCharacter();
        this.$notify(`${label} updated.`);
      } catch (error) {
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(error.message || error);
      } finally {
        this.uploadingField = null;
        this.clearInput(field);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-actions {
  gap: 1rem;
  display: flex;
  align-items: center;
}

.detail-value {
  padding-top: 0.5rem;
}

.autosave-note {
  margin-top: 1rem;
  color: #76838f;
  font-size: 0.9rem;
}

.upload-card {
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #dfe4ea;
  background: #fff;
}

.upload-preview-wrapper {
  display: flex;
  min-height: 240px;
  margin-top: 1rem;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px dashed #dfe4ea;
}

.upload-preview {
  max-width: 100%;
  max-height: 220px;
  object-fit: contain;
}

.preview-placeholder {
  color: #90a4ae;
  font-size: 0.9rem;
  text-align: center;
}

.upload-helper {
  color: #76838f;
  font-size: 0.85rem;
}

.slot-grid {
  gap: 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.upload-card-header {
  gap: 0.75rem;
  display: flex;
  justify-content: space-between;
}

.uploading-text {
  color: #8a5a00;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.file-input-row {
  gap: 0.75rem;
  display: flex;
  min-height: 38px;
  margin-top: 1rem;
  align-items: center;
  padding: 0.75rem 0.875rem;
  border-radius: 10px;
  border: 1px solid #dfe4ea;
  background: #fafbfc;
}

.file-input-name {
  color: #76838f;
  font-size: 0.9rem;
  line-height: 1.4;
  word-break: break-word;
}

.file-input-native {
  display: none;
}

.save-badge {
  min-width: 96px;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  text-align: center;
  font-weight: 600;
  font-size: 0.875rem;
  background: #edf1f7;
  color: #526069;

  &.is-saved {
    color: #0b6b2b;
    background: #e7f7ed;
  }

  &.is-saving {
    color: #8a5a00;
    background: #fff4d8;
  }

  &.is-unsaved {
    color: #a94442;
    background: #fde8e7;
  }
}
</style>
